<script>
export default {
  title: 'Assignment 4.2: Cat and Mouse',
  starterId: 'assign4.2.zip'
}
</script>

<template>
  <section>

    <Readings :readings="[
      {
        title: '<cite>Advanced Bash Scripting Guide</cite> Sections 9-13, 25-26, 29-34',
        link: 'http://www.tldp.org/LDP/abs/html'
      }
    ]"/>

    <article>
      <h2>Overview</h2>
      <p>In this assignment, you will use the math library you created in the
        previous assignment to write a BASH script for a simulation.</p>
    </article>

    <article id="starter">
      <h2>Starter Code</h2>

      <p>
        Please download the starter code from above. It should contains a single file
        called <code>catmouse-fw.sh</code>. You will also need <code>bash-functions.sh</code>
        from your previous assignment and copy it into the same directory.
      </p>
    </article>

    <article>
      <h2>Cat and Mouse</h2>

      <figure>
        <img src="./images/cat-and-mouse.png" alt="Cat and Mouse" class="img-responsive center-block" width="560">
        <figcaption>Figure 1. The Cat and Mouse problem.</figcaption>
      </figure>

      <p>The scene is an urban park; a cat watches a mouse run around the base of
        a statue of the actors from Monty Python. Over the course of a second, the
        somewhat witless mouse moves one meter counterclockwise around the
        statue&rsquo;s base, which is circular and two meters in diameter. Every
        second, the cat pursues the mouse as follows: If the cat can see the mouse,
        the cat moves one meter toward the statue. If the cat can&rsquo;t see the
        mouse, the cat circles 1.25 meters counterclockwise around the statue.</p>

      <p>The cat plans eventually to get close enough to the mouse to make it a
        juicy lunch. The mouse by accident, however, may manage to keep completely
        out of sight of the cat, since both the cat and the mouse are moving
        counter-clockwise.</p>

      <p>Should the chase go on more than 30 seconds, however,
        the cat will get tired and wander off.</p>
    </article>

    <article>
      <h2>Why use BASH?</h2>
      <p>Indeed it is true that most experienced programmers, even those
        extremely versed in shell scripting, would not choose BASH to tackle a
        problem like this. However, the main reasons against using BASH for this
        type of problem in the real world present us with a unique educational
        opportunity to explore different aspects of shell programming. The mere
        fact that a simulation like this can be done using BASH and UNIX tools
        shows the power and versitility that shell programming has to offer.</p>
    </article>

    <article>
      <h2>Trigonometric Information</h2>
      <ul>
        <li>
          The cat sees the mouse if <br>
          <code>(cat radius) * cos (cat angle - mouse angle) >= 1.0</code> (angles in radian)
        </li>
        <li>
          When the cat circles distance d around the statue, its radius does not change, and the change in
          its angle can be calculated from the relationship <br>
          <code>d = angle * radius</code> (angles in radian)
        </li>
        <li>
          The cat catches the mouse when it (the cat) moves past the mouse while at the base of the statue,
          i.e. when the cat radius is 1.0 and the mouse angle lies between the old cat angle and the new
          cat angle. An angle B is between angles A and C in the following circumstances:<br>
          <code>cos (B - A) &gt; cos (C - A)</code>, and <code>cos (C - B) &gt; cos (C - A)</code> (angles in radian)<br>
          The difference C - A is assumed to be less than 90&deg;, or &pi;/2 radians.
        </li>
        <li>Note that the cat cannot move inside the statue&rsquo;s base; hence if the cat is, say, at radius 1.7 and sees the mouse, it can move in only 0.7 meters, up to the base of the statue.
        </li>
        <li>Remember that angles a, a + 2&pi;, a + 4&pi;, ... are all equal.</li>
      </ul>
    </article>

    <article>
      <h2>Part 1: Floating Point Math</h2>
      <p>You should use the bashcalc-functions.sh file that you created in the
        previous assignment to provide basic math functionality in your Cat &amp;
        Mouse simulator. Do this by including a line at the top of your script to
        source the file:</p>
      <pre v-highlightjs><code class="bash">. bashcalc-functions.sh</code></pre>

      <p>Now, implement the following two functions which will be used later on
        in the simulation:</p>
      <ul>
        <li>angle_between (Hint: Look in the Trig Information above)</li>
        <li>does_cat_see_mouse (Hint: Look in the Trig Information above)</li>
      </ul>

      <p>Bring in transcripts showing your tests for both of these functions.
        Make sure you test sufficient cases to be satisfied with the function's
        correctness.</p>
    </article>

    <article>
      <h2>Part 2: Simulation Step</h2>

      <p>Now that you have the helper functions you need, it's time to write the
        heart of the simulation. Within the framework script, we have provided a
        framework for the function next_step. This function takes in the current
        state, step number, cat and mouse positions, and max steps and prints the
        state at the next step of the simulation.</p>

      <p>Please complete the implementation of this function (you may start from
        scratch if you wish) and perform sufficient testing to catch all possible
        corner cases. Bring in a transcript of this testing for your tutor to
        examine.</p>

      <p>Clarification on possible ambiguity:</p>
      <ul>
        <li>In each step, the cat moves first. If it moves past the mouse, it catches it.</li>
        <li>If the cat didn't catch the mouse, the mouse then moves.</li>
      </ul>
    </article>

    <article>
      <h2>Part 3: Putting It All Together</h2>

      <p>And for the final step, you now need to make an executable script out of
        these functions which takes in the initial positions and a maximum number
        of steps to allow.  Your script should print out the state
        (running/caught/given up) and positions at each step of the simulation.</p>

      <p>Create a transcript of your tests of the simulator, and bring them in
        for your tutor to see.  You should show at least one test case in which the
        mouse is caught without any movement (they start at the same location), one
        in which the cat catches the mouse on the very last step, and one in which
        the cat gives up but would've caught the mouse if he took one more
        step.</p>
    </article>

    <article>
      <h2>Testing</h2>

      <p>Test each function and method in your program with enough different
        inputs to show that it works. When testing a method, print out the input,
        the expected result, and the actual result.</p>

      <p>It would probably be helpful for debugging purposes to have a debug flag
        available that, when true, dumped the position and heading of all
        characters involved (cat and mouse) at each time step.</p>

      <p>You should test the methods of each Class in isolation, using
        appropriate test data, to see if they work independently before calling
        them from a driver function. Tutors may ask to see the results of these
        tests. Your test data should include the following values:</p>

      <p><i>(Note: Cat radius is calculated from the center of Statue)</i></p>

      <table class="table table-bordered table-striped">
        <tr>
          <th align="center">Cat Radius (m)</th>
          <th align="center">Cat Angle (degree)</th>
          <th align="center">Mouse Angle (degree)</th>
          <th align="center">Test Case Features</th>
        </tr>
        <tr>
          <td align="center">1.0</td>
          <td align="center">35.0&deg;</td>
          <td align="center">396.0&deg;</td>
          <td align="center">angle &gt; 360; mouse caught immediately</td>
        </tr>
        <tr>
          <td align="center">3.2</td>
          <td align="center">0.0&deg;</td>
          <td align="center">-57.0&deg;</td>
          <td align="center">negative angle;<br>
            cat radius has fractional part;<br>
            mouse caught at time 17</td>
        </tr>
        <tr>
          <td align="center">8.1</td>
          <td align="center">0.0&deg;</td>
          <td align="center">45.0&deg;</td>
          <td align="center"></td>
        </tr>
        <tr>
          <td align="center">8.1</td>
          <td align="center">150.0&deg;</td>
          <td align="center">240.0&deg;</td>
          <td align="center"></td>
        </tr>
        <tr>
          <td align="center">4.0</td>
          <td align="center">0.0&deg;</td>
          <td align="center">-57.0&deg;</td>
          <td align="center"></td>
        </tr>
      </table>
      <p>...plus the following cases:</p>
      <ul>
        <li>cat catches mouse at time 30;</li>
        <li>cat would catch mouse at time 31 if the simulation ran that long;</li>
        <li>whatever other values are necessary to ensure that all statements in your program have been executed at least once.</li>
      </ul>

      <p>Be sure to check that the output makes sense for these tests, since the output is what most tutors will check first. Students are sometimes embarrassed to have a tutor point out that their programs have the cat moving deeper and deeper inside the statue, or moving away from the mouse instead of approaching it.</p>

    </article>

    <article>
      <h2>Checklist</h2>

      <article>
        <h3>Part 1</h3>
        <ul>
          <li>Student has correctly working code.</li>
          <li>Student shows sufficient test cases for all helper functions.</li>
        </ul>
      </article>

      <article>
        <h3>Part 2</h3>
        <ul>
          <li>Student has correctly working code.</li>
          <li>Student shows sufficient test cases to catch possible corner cases.</li>
        </ul>
      </article>

      <article>
        <h3>Part 3</h3>
        <ul>
          <li>Student has correctly working code.</li>
          <li>Student shows sufficient test cases to catch possible corner cases:
            <ul>
              <li>Catching the mouse at start</li>
              <li>Catching the mouse on the max step</li>
              <li>Catching the mouse on the max step - 1 (thus giving up)</li>
              <li>etc</li>
            </ul>
          </li>
        </ul>

        <article>
          <h3>All</h3>
          <ul>
            <li>Student correctly uses quotes where appropriate</li>
            <li>Student correctly uses integer, text, and floating point comparisons</li>
          </ul>
        </article>
      </article>
    </article>
  </section>
</template>



// WEBPACK FOOTER //
// src/components/assignments/unix/CatAndMouse.vue